import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/format";

type Supplier = components["schemas"]["SupplierResponse"];

function formatAddress(addr: Supplier["address"]): string | null {
  if (!addr) return null;
  const parts = [addr.line1, addr.line2, addr.city, addr.postalCode, addr.country].filter(
    (p): p is string => Boolean(p),
  );
  return parts.length ? parts.join(", ") : null;
}

export function SupplierPage() {
  const { supplierId } = useParams<{ supplierId: string }>();

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supplierId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function fetchSupplier() {
      setLoading(true);
      const { data, error: apiError } = await api.GET("/admin/suppliers/{id}", {
        params: { path: { id: supplierId! } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load supplier.");
      } else {
        setSupplier(data ?? null);
      }
      setLoading(false);
    }

    fetchSupplier();
    return () => {
      cancelled = true;
    };
  }, [supplierId]);

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Supplier</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : supplier ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {supplier.name || <span className="text-muted-foreground">Unnamed</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Section title="Supplier">
              <Field label="Supplier ID">
                <Badge variant="secondary" className="font-mono text-xs">
                  {supplier.id}
                </Badge>
              </Field>
              <Field label="Organisation">
                {supplier.organisationId ? (
                  <Link to={`/organisations/${supplier.organisationId}`}>
                    <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                      {supplier.organisationId}
                    </Badge>
                  </Link>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Field>
              <Field label="Payment terms">{supplier.paymentTermsDefault ?? "—"}</Field>
            </Section>

            <Section title="Contact">
              <Field label="Email">{supplier.email ?? "—"}</Field>
              <Field label="Phone">{supplier.phone ?? "—"}</Field>
              <Field label="Website">
                {supplier.website ? (
                  <a
                    href={supplier.website}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="hover:underline"
                  >
                    {supplier.website}
                  </a>
                ) : (
                  "—"
                )}
              </Field>
              <Field label="Tax number">{supplier.taxNumber ?? "—"}</Field>
              <Field label="Registration number">{supplier.registrationNumber ?? "—"}</Field>
              <Field label="Address" className="sm:col-span-3">
                {formatAddress(supplier.address) ?? "—"}
              </Field>
            </Section>

            {supplier.bankDetails && (
              <Section title="Bank details">
                <Field label="Account holder">
                  {supplier.bankDetails.accountHolderName ?? "—"}
                </Field>
                <Field label="Account number">
                  {supplier.bankDetails.accountNumber ?? "—"}
                </Field>
                <Field label="Sort code">{supplier.bankDetails.sortCode ?? "—"}</Field>
                <Field label="Routing number">
                  {supplier.bankDetails.routingNumber ?? "—"}
                </Field>
                <Field label="IBAN">{supplier.bankDetails.iban ?? "—"}</Field>
                <Field label="BIC / SWIFT">{supplier.bankDetails.bicSwift ?? "—"}</Field>
                <Field label="Country">{supplier.bankDetails.country ?? "—"}</Field>
                <Field label="Currency">{supplier.bankDetails.currency ?? "—"}</Field>
              </Section>
            )}

            <Section title="Audit">
              <Field label="Created">{formatDateTime(supplier.createdAt)}</Field>
              <Field label="Created by">
                <ActorLink actorId={supplier.createdBy} />
              </Field>
              <Field label="Updated">{formatDateTime(supplier.updatedAt)}</Field>
              <Field label="Updated by">
                <ActorLink actorId={supplier.updatedBy} />
              </Field>
            </Section>
          </CardContent>
        </Card>
      ) : (
        <p className="text-muted-foreground">Supplier not found.</p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">{children}</dl>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function ActorLink({ actorId }: { actorId: string | null | undefined }) {
  if (!actorId) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/users/${actorId}`}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {actorId.slice(0, 8)}...
      </Badge>
    </Link>
  );
}
